import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Spinner } from '../../shared-components/decorators/spinner';

import { IDV_TRIGGER_TYPES } from './idv.consts';
import { withIDV } from './with-idv.hoc';

/**
 * @function
 * @description A React HOC that checks user's profile either the user has an access to the component or not.
 * @description Blocks render of the component until IDV completed
 * @name withIDV
 * @param {object} Component
 * @param {{
 *   // IDV_TRIGGER_TYPES (./idv.consts.js)
 *   triggerType: string/undefined,
 *   // TODO find a better way to separate /account/bank from other flows
 *   shouldSkip: boolean/undefined,
 *   cancelUrl: string/undefined
 * }} options
 */
const requireIDV = (Component, {
  triggerType = IDV_TRIGGER_TYPES.bank_account,
  shouldSkip = false,
  cancelUrl = '/account/methods'
} = {}) => {
  const HOC = (props) => {
    const { idv, hasBank, hasAccountFetched } = props;

    if (!idv) {
      throw new Error('requireIDV must be wrapped within withIDV HOC');
    }

    const { hasValidVerification, hasLoaded: hasIdvLoaded } = idv;

    // If flag set to true, that means we are safe to render content
    const [success, setSuccess] = React.useState(shouldSkip);

    // Show IDV modal if component is protected by this HOC and user doesn't have a valid session
    React.useEffect(() => {
      if (!hasIdvLoaded || shouldSkip || !hasAccountFetched) {
        return;
      }

      const { showIDVModal } = idv;

      if (!hasValidVerification && hasBank) {
        showIDVModal({
          closable: false,
          onSuccess: () => {
            setSuccess(true);
          },
          onBackClick: () => {
            window.location.href = cancelUrl;
          }
        });
      } else {
        setSuccess(true);
      }
    }, [hasIdvLoaded, hasValidVerification, shouldSkip, hasBank, hasAccountFetched]);

    // Block content from rendering until it is safe
    if (!success) {
      if (!hasIdvLoaded || !hasAccountFetched) {
        return (
          <div className="spinner-container padding-xl-top">
            <Spinner additionalStyle="gray" />
          </div>
        );
      }
      return null;
    }

    return <Component {...props} />;
  };

  HOC.propTypes = {
    profile: PropTypes.object,
    idv: PropTypes.object,
    hasBank: PropTypes.bool,
    hasAccountFetched: PropTypes.bool
  };

  const mapStateToProps = (state) => {
    const { nav } = state;

    const hasBank = !isEmpty(nav?.managedAccount?.external_accounts?.data?.[0]);
    const hasAccountFetched = nav?.managedAccountFetched ?? false;

    return {
      hasBank,
      hasAccountFetched
    };
  };

  return connect(mapStateToProps)(withIDV(HOC, { triggerType }));
};

export { requireIDV };
